<template>
  <!-- 资金流向 -->
  <div class="capital-holding-wrapper edge-width-padding">
    <MoreTitle title="最近5日主力增减仓" no-split no-edge-padding />
    <div class="time-section">
      <div class="legend-wrap">
        <span class="legend-item legend-item--in">{{ $t('资金流入') }}</span>
        <span class="legend-item legend-item--out">{{ $t('资金流出') }}</span>
      </div>
      <span class="unit">{{ $t('单位') }}：{{ $t(divisorUnit[1]) }}</span>
    </div>
    <VueEcharts :option="option" />
  </div>
</template>

<script>
import VueEcharts from '@/components/common/VueEcharts.vue'
import { postcssPatch } from '@/utils/postcssPatch'
export default {
  components: { VueEcharts },
  inject: ['theme'],
  props: ['getUnit'],
  data() {
    return {
      holdingData: [],
      intervalID: null,
      tradeStaus: []
    }
  },
  computed: {
    isUs() {
      return this.$route.query?.symbol?.includes('.us')
    },
    symbol() {
      return this.$route.query.symbol
    },
    timeMode() {
      // 有效值为 '0': 盘中延时 '1': 盘中实时，
      return this.$route.query.timeMode
    },
    market() {
      return this.$funs.getMarket(this.symbol)
    },
    divisorUnit() {
      const capitalArr = this.holdingData.map((item) => item.value)
      return this.getUnit(capitalArr)
    },
    syncHoldingData() {
      const divisor = this.divisorUnit[0]
      return this.holdingData.map((item) => ({
        name: item.name,
        value: this.$digitjs(item.value).convert(this.isUs ? 4 : 2, divisor)
      }))
    },
    option() {
      return {
        grid: {
          top: postcssPatch(15),
          bottom: postcssPatch(15),
          left: 0,
          right: 0,
          containLabel: true
        },
        visualMap: {
          show: false,
          pieces: [
            { gt: 0, color: this.theme['--up-color'] },
            { lt: 0, color: this.theme['--down-color'] },
            { value: 0, color: this.theme['--flat-color'] }
          ]
        },
        dataset: {
          source: this.syncHoldingData
        },
        xAxis: {
          type: 'category',
          inverse: true,
          axisTick: {
            show: false
          },
          axisLabel: {
            interval: 0,
            margin: 20,
            color: this.theme['--font-color-3'],
            formatter: (value) => {
              return value ? this.$dayjs(value).format('MM-DD') : ''
            }
          },
          axisLine: {
            lineStyle: {
              color: this.theme['--border-color-1']
            }
          }
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            show: false
          },
          splitLine: {
            show: false
          }
        },
        series: [
          {
            type: 'bar',
            barWidth: 15,
            encode: {
              x: 'name',
              y: 'value'
            },
            label: {
              show: true,
              color: 'inherit',
              fontSize: 12,
              position: 'bottom'
            },
            labelLayout({ text, rect, labelRect }) {
              const value = Number(text)
              if (value < 0) {
                const diffWidth = (labelRect.width - rect.width) / 2
                return {
                  x: rect.x - diffWidth,
                  y: rect.y - 13
                }
              }
            }
          }
        ]
      }
    }
  },
  created() {
    if (this.market === 'jp') {
      this.getTradeStatus().then(() => {
        // 10s 请求一次接口，模拟实时推送
        if (this.timeMode === '0') {
          this.getCapitalHolding()
          this.intervalID = setInterval(() => {
            this.getCapitalHolding()
          }, 10000)
        } else {
          this.getCapitalHolding()
        }
      })
    } else {
      this.getCapitalHolding()
    }
  },
  destroyed() {
    if (this.intervalID) {
      clearInterval(this.intervalID)
    }
  },
  methods: {
    async getTradeStatus() {
      const res = await this.$post(this.$api.sdk[this.market] + '/utils/tradeStatus', {
        orderMode: 0,
        pageNum: 1,
        pageSize: 10
      })
      const data = res?.data?.records

      if (data) {
        this.tradeStatus = data.filter((r) => r.status === 'Y').slice(0, 5)
      }
    },

    async getCapitalHolding() {
      const res = await this.$post(this.$api.sdk[this.market] + '/capital/flow/daily', { symbol: this.symbol })
      const data = res.body ?? res.data ?? []
      let holdingData = data

      // 当日股的最近五日主力增减仓不够五日时，采用查询交易日补上日期
      if (data.length < 5 && this.market === 'jp') {
        holdingData = this.tradeStatus.map((item) => {
          const findData = data.find((r) => this.$dayjs(r.date).isSame(this.$dayjs(item.date)))

          return findData ?? item
        })
      } else {
        // TODO: 接口重新设计，支持传入排序参数，根据参数返回对应参数的排序顺序
        const sortData = data.sort((a, b) => b.date.localeCompare(a.date))
        holdingData = sortData.slice(0, 5)
      }
      this.holdingData = holdingData.map((item) => ({
        name: item.date,
        value: (item.capitalLargeFunds ?? 0) + (item.capitalBigFunds ?? 0)
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.capital-holding-wrapper {
  margin-top: 20px;
  background: var(--bg-color-2);
  .time-section {
    display: flex;
    align-items: flex-end;
    margin-bottom: 15px;
    font-size: 12px;
    .unit {
      margin-left: auto;
      color: var(--font-color-3);
    }
  }
  .legend-wrap {
    display: flex;
  }
  .legend-item {
    display: flex;
    align-items: center;
    color: var(--font-color-2);
    &::before {
      content: '';
      width: 10px;
      height: 10px;
      margin-right: 5px;
    }
  }
  .legend-item--in {
    margin-right: 20px;
    &::before {
      background: var(--up-color);
    }
  }
  .legend-item--out {
    &::before {
      background: var(--down-color);
    }
  }
}
</style>
